import { LoggingFacade } from '@vwdfive/myvw-logging';
import { IVehicle } from '../gvf';
import { TTDSGConsentClient } from './TTDSGConsentClient';
import {
    ConsentsVehicle,
    ConsentsVehicleResponse,
    Decision,
    DocumentWithVin,
    EnrichedDocument,
    ICreateConsentDecisionsPayload,
} from './types';
import { DecisionType } from '../enums';

export class TTDSGConsentService {
    constructor(
        private readonly ttdsgConsentClient: TTDSGConsentClient,
        private readonly loggingFacade: LoggingFacade,
    ) {}

    public checkIsConsentDue(documentsForVehicles: ConsentsVehicle[]) {
        let isConsentDue = false;
        if (!documentsForVehicles.length) {
            return isConsentDue;
        }
        for (let i = 0; i < documentsForVehicles.length; i++) {
            const vehicle = documentsForVehicles[i];
            if (vehicle.consentDecisionRequired) {
                isConsentDue = true;
                break;
            }
        }
        return isConsentDue;
    }

    private getFilteredDocumentsGroupedByVehicles(data: ConsentsVehicle[]) {
        return data
            .filter((vehicle) => vehicle.consentDecisionRequired)
            .map((vehicle) => ({
                ...vehicle,
                documents: vehicle.documents.filter((document) => document.consentDocumentDecisionRequired),
            }));
    }

    public async getDocumentsConsentDecisionRequiredGroupedByVehicle(isWcar: boolean, userId: string) {
        try {
            return this.getFilteredDocumentsGroupedByVehicles(
                await this.ttdsgConsentClient.getDocumentConsentVehicle(isWcar, userId),
            );
        } catch (error: any) {
            this.loggingFacade.error(`Could not fetch documents from ttdsg-consent`, error as Error);
            throw new Error(error);
        }
    }

    public async getDocumentsGroupedByVehicles(isWcar: boolean, userId: string, vehicles: IVehicle[]) {
        try {
            const data: ConsentsVehicleResponse = await this.ttdsgConsentClient.getDocumentConsentVehicle(isWcar, userId);
            return data.map((item) => ({ ...item, vehicle: vehicles.find((vehicle) => vehicle.vin === item.vin) }));
        } catch (error: any) {
            this.loggingFacade.error(`Could not fetch documents from ttdsg-consent`, error as Error);
            throw new Error(error);
        }
    }

    private getAllDocuments(data: ConsentsVehicleResponse) {
        const result: DocumentWithVin[] = [];

        if (!data) {
            return result;
        }

        data.forEach((item) => {
            item.documents.forEach((document) => {
                result.push({
                    userId: item.userId,
                    consentDecisionRequired: item.consentDecisionRequired,
                    consentDocumentDecisionRequired: document.consentDocumentDecisionRequired,
                    ...document.versionDetails,
                    vin: item.vin,
                    consent: document.consent,
                });
            });
        });
        return result;
    }

    private getVinsByDocumentId(documentId: string, data: ConsentsVehicleResponse) {
        const allDocuments = this.getAllDocuments(data);
        return allDocuments.filter((document) => document.id === documentId).map((documentVin) => documentVin.vin);
    }

    private getVehiclesByDocumentId(documentId: string, vehicles: IVehicle[], data: ConsentsVehicleResponse) {
        const vins = this.getVinsByDocumentId(documentId, data);

        const result: IVehicle[] = [];
        vins.forEach((vin) => {
            const v = vehicles.find((v) => v.vin === vin);
            if (v) {
                result.push(v);
            }
        });
        return result;
    }

    public getUniqueDocuments(data: ConsentsVehicleResponse, vehicles: IVehicle[]) {
        const allDocuments = this.getAllDocuments(data);
        const result: EnrichedDocument[] = [];

        allDocuments.forEach((document) => {
            if (!result.some((item) => item.id === document.id)) {
                const {vin, ...rest} = document;
                result.push({
                    ...rest,
                    vehicles: this.getVehiclesByDocumentId(document.id, vehicles, data),
                });
            }
        });
        return result;
    }

    public async createConsents(payload: ICreateConsentDecisionsPayload[], isWcar: boolean, userId: string) {
        for (const consent of payload)
        {
            try {
                await this.ttdsgConsentClient.createConsent(consent, isWcar, userId);
            } catch (error: any) {
                this.loggingFacade.error(`Could not fetch documents from ttdsg-consent`, error as Error);
                throw new Error(error);
            }
        }
    }

    public createDecisions(existingDecisions: Decision[], newDecision: Decision) {
        const existingItem = existingDecisions.find(
            (d) => d.versionDetailsId === newDecision.versionDetailsId && d.vin === newDecision.vin,
        );
        if (existingItem) {
            existingItem.decision = newDecision.decision;
            return existingDecisions;
        }
        return existingDecisions.concat(newDecision);
    }

    public createConsentPayload(
        initialData: ConsentsVehicleResponse,
        decisions: Decision[],
        decisionType: DecisionType,
    ) {
        return initialData.map((item) => ({
            userId: item.userId,
            vin: item.vin,
            consents: item.documents.map((document) => ({
                versionDetailsId: document.versionDetails.id,
                decision:
                    decisionType === DecisionType.ACCEPT_ALL
                        ? true
                        : decisionType === DecisionType.DECLINE_ALL
                            ? false
                            : decisions.find(
                            (decision) =>
                                decision.vin === item.vin &&
                                decision.versionDetailsId === document.versionDetails.versionId,
                        )?.decision ?? false,
            })),
        }));
    }
}
